import { getLocale } from "@/lib/i18n";

// a07c — French for the configurator-written text the Results page shows
// (candidate names, equipment list, cost categories, energy sources). The
// report's own French terms are used. EN passes through unchanged.
const fr = () => getLocale() === "fr";

// Same tables as backend report_v3/i18n.py CANDIDATE_NAME_HEAD_FR / _QUALIFIER_FR.
const HEAD = {
  "Solar + battery + diesel": "Solaire + batterie + diesel",
  "Solar + battery + gas engines": "Solaire + batterie + moteurs à gaz",
  "Solar + large gas engines": "Solaire + grands moteurs à gaz",
  "Solar + gas engines": "Solaire + moteurs à gaz",
  "Solar + diesel": "Solaire + diesel",
  "Solar + grid backstop": "Solaire + secours réseau",
  "Solar + battery only": "Solaire + batterie uniquement",
  "Solar + battery": "Solaire + batterie",
  "Full-thermal baseload": "Centrale tout-thermique en base",
  "Diesel gensets": "Groupes diesel",
  "Diesel-only": "Diesel uniquement",
};
const QUAL = [
  ["no thermal, off-grid", "sans thermique, hors réseau"],
  ["diesel gensets", "groupes diesel"],
  ["gas engines", "moteurs à gaz"],
  ["no battery", "sans batterie"],
  ["no thermal", "sans thermique"],
  ["off-grid", "hors réseau"],
];

// B2 (30 Oct) — "large" is not a size: the solar + gas-engine plant is named like the others.
export function plantDisplayName(name, sizing) {
  if (name !== "Solar + large gas engines") return name;
  const n = Number(sizing?.gas_units || 0), u = Number(sizing?.gas_unit_size_kw || 0);
  if (!n || !u) return "Solar + gas engines";
  const unitTxt = u >= 1000 ? `${Math.round(u / 10) / 100} MW` : `${Math.round(u)} kW`;
  return `Solar + gas engines (${n} × ${unitTxt}, N+1)`;
}

export function candidateName(name, sizing) {
  name = plantDisplayName(name, sizing);
  if (!fr() || !name) return name;
  const i = name.indexOf(" (");
  const head = i < 0 ? name : name.slice(0, i);
  const h = HEAD[head.trim()];
  if (!h) return name;
  if (i < 0) return h;
  let tail = name.slice(i + 2).replace(/\)$/, "");
  QUAL.forEach(([en, f]) => { tail = tail.split(en).join(f); });
  return `${h} (${tail})`;
}

const CATEGORY = {
  Generation: "Production", "Power conversion": "Conversion", Storage: "Stockage",
  Controls: "Contrôles", BoP: "BoP", Civils: "Génie civil", "Layout note": "Note d'implantation",
};
const ITEM = {
  "Monocrystalline PV array (bifacial where applicable)": "Champ PV monocristallin (bifacial le cas échéant)",
  "String inverters, MPPT combiners, DC/AC protections": "Onduleurs string, coffrets MPPT, protections DC/AC",
  "Li-ion LFP battery bank with BMS + fire protection": "Banque batterie Li-ion LFP avec BMS + protection incendie",
  "Bidirectional hybrid inverter / PCS": "Onduleur hybride bidirectionnel / PCS",
  "Small-medium wind turbines": "Éoliennes de petite et moyenne taille",
  "HFO reciprocating engine": "Moteur alternatif HFO",
  "Hybrid power plant controller with SCADA, remote monitoring, grid-tie functions":
    "Contrôleur de centrale hybride avec SCADA, supervision à distance, fonctions de couplage réseau",
  "MV/LV switchgear, transformers (as applicable), cables, protections":
    "Appareillage HTA/BT, transformateurs (le cas échéant), câbles, protections",
  "Mounting structures, foundations, fencing, cable trenches, control room":
    "Structures de montage, fondations, clôtures, tranchées de câbles, salle de contrôle",
  "Thermal plant controller with SCADA, remote monitoring, grid-tie functions":
    "Contrôleur de centrale thermique avec SCADA, supervision à distance, fonctions de couplage réseau",
  "Foundations, fencing, cable trenches, control room":
    "Fondations, clôtures, tranchées de câbles, salle de contrôle",
};

// Thermal machine rows carry an optional " — N × U kW" band after the name.
const THERMAL_ITEM = [
  [/^Gas reciprocating engine \(natural gas \/ LPG\)/, "Moteur alternatif à gaz (gaz naturel / GPL)"],
  [/^Open-cycle gas turbine \(OCGT\)/, "Turbine à gaz à cycle ouvert (OCGT)"],
  [/^Combined-cycle gas turbine \(CCGT\)(.*) with heat-recovery steam generator and steam turbine$/,
    "Turbine à gaz à cycle combiné (CCGT)$1 avec chaudière de récupération et turbine à vapeur"],
  [/^Diesel black-start genset \(standby\)/, "Groupe diesel de démarrage autonome (secours)"],
];

const LAYOUT_BITS = {
  "approx. 6–8 m² of ground/roof per kWp": "env. 6–8\u00a0m² de sol/toiture par kWc",
  "genset compound with acoustic enclosures and setback per local codes":
    "enceinte des groupes avec capotages acoustiques et distances de recul selon les codes locaux",
  "generating-unit hall or compound with acoustic enclosures, fuel supply and metering, exhaust stack and setback per local codes":
    "salle ou enceinte des unités de production avec capotages acoustiques, alimentation et comptage du combustible, cheminée d'échappement et distances de recul selon les codes locaux",
};
const LAYOUT_RE = /^Site layout to be developed in feasibility phase — (.*)\.$/;

export const frNum = (s) => s.replace(/(\d),(\d{3})/g, "$1\u00a0$2").replace(/(\d)\.(\d)/g, "$1,$2").replace(/\b([kM])Wp\b/g, "$1Wc").replace(/(\d) (kWc|MWc|kWh|kW|MW|m²)/g, "$1\u00a0$2");

// a37 — engine sizes like "30082.2 kWh" get a thousands separator.
const groupThousands = (s) => String(s).replace(/(^|[^\d.,])(\d{4,})(?=[.\s]|$)/g, (_, p, i) => p + i.replace(/\B(?=(\d{3})+(?!\d))/g, ","));

export function equipmentRow(e) {
  if (!fr()) return e.size ? { ...e, size: groupThousands(e.size) } : e;
  let item = ITEM[e.item] || e.item;
  // "Diesel gensets — 8 × 100 kW (parallelled, N+1 configuration where applicable)"
  item = item.replace(/^Diesel gensets — (.*) \(parallelled, N\+1 configuration where applicable\)$/,
    "Groupes diesel — $1 (en parallèle, configuration N+1 le cas échéant)");
  THERMAL_ITEM.forEach(([re, f]) => { item = item.replace(re, f); });
  const lay = item.match(LAYOUT_RE);
  if (lay) item = `Implantation à développer en phase de faisabilité — ${lay[1].split("; ").map((b) => LAYOUT_BITS[b] || b).join("\u00a0; ")}.`;
  const size = { "1 set": "1 ensemble", "1 lot": "1 lot" }[e.size] || frNum(groupThousands(e.size || ""));
  return { ...e, category: CATEGORY[e.category] || e.category, item: frNum(item), size };
}

const TECH = {
  "Solar PV": "Solaire PV", "Li-ion BESS": "BESS Li-ion", Wind: "Éolien", "Diesel gensets": "Groupes diesel",
  "HFO genset": "Groupe électrogène HFO", "Gas reciprocating engines": "Moteurs alternatifs à gaz",
  "Diesel black-start": "Diesel de démarrage autonome",
};

export function capexTech(name) {
  if (!fr() || !name) return name;
  const pctFr = (x) => x.replace(/\s*\(\+?(\d+)%\)/, (m, n) => ` (+${n}\u00a0%)`);
  if (/^BoP/.test(name)) return pctFr(name.replace(/^BoP \/ EPC \/ Soft ?costs/i, "BoP / EPC / coûts indirects"));
  if (/^Contingency/.test(name)) return pctFr(name.replace(/^Contingency\s*/, "Imprévus"));
  const i = name.indexOf(" (");
  const head = i < 0 ? name : name.slice(0, i);
  return TECH[head] ? TECH[head] + frNum(i < 0 ? "" : name.slice(i)) : name;
}

// a38 — "Gas reciprocating engines (6 × 250 kW = 1,500 kW)"; other rows keep "(size)", grouped.
export function capexRowLabel(b) {
  const tech = capexTech(b.tech);
  if (!b.size) return tech;
  const size = fr() ? frNum(groupThousands(String(b.size))) : groupThousands(String(b.size));
  const m = tech.match(/^(.*\(\d+ × [^(),=]+)\)$/);
  return m ? `${m[1]} = ${size})` : `${tech} (${size})`;
}

const SOURCE = { solar: "Solaire", battery: "Batterie", wind: "Éolien", diesel: "Diesel", grid: "Réseau",
  gas: "Gaz", hfo: "HFO", thermal: "Thermique" };
export const sourceName = (k) => (fr() && SOURCE[k]) || k[0].toUpperCase() + k.slice(1);

const SECTOR = { Industrial: "Industrie", Mining: "Mines", Utilities: "Secteur électrique", Telecom: "Télécoms" };
export const sectorName = (s) => (fr() && SECTOR[s]) || s;
